export type SoundEffectPreset = 'tap' | 'navigate' | 'toggle' | 'select' | 'success';

const SOUND_EFFECTS_KEY = 'soundEffectsEnabled';
const LEGACY_MUSIC_KEY = 'musicEnabled';
const DEFAULT_ENABLED = true;

type WebAudioWindow = Window & typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
};

interface ToneOptions {
    startTime: number;
    duration: number;
    fromFrequency: number;
    toFrequency: number;
    volume: number;
    type?: OscillatorType;
}

class SoundEffectsServiceClass {
    private audioContext: AudioContext | null = null;
    private enabled = this.readEnabledFromStorage();

    private isBrowser(): boolean {
        return typeof window !== 'undefined';
    }

    private readEnabledFromStorage(): boolean {
        if (!this.isBrowser()) {
            return DEFAULT_ENABLED;
        }

        const explicitSetting = localStorage.getItem(SOUND_EFFECTS_KEY);
        if (explicitSetting !== null) {
            return explicitSetting === 'true';
        }

        const legacySetting = localStorage.getItem(LEGACY_MUSIC_KEY);
        if (legacySetting !== null) {
            return legacySetting === 'true';
        }

        return DEFAULT_ENABLED;
    }

    private getAudioContext(): AudioContext | null {
        if (!this.isBrowser()) {
            return null;
        }

        if (this.audioContext) {
            return this.audioContext;
        }

        const audioWindow = window as WebAudioWindow;
        const AudioContextCtor = audioWindow.AudioContext ?? audioWindow.webkitAudioContext;
        if (!AudioContextCtor) {
            return null;
        }

        this.audioContext = new AudioContextCtor();
        return this.audioContext;
    }

    private scheduleTone(context: AudioContext, options: ToneOptions) {
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        const endTime = options.startTime + options.duration;

        oscillator.type = options.type ?? 'triangle';
        oscillator.frequency.setValueAtTime(options.fromFrequency, options.startTime);
        oscillator.frequency.exponentialRampToValueAtTime(options.toFrequency, endTime);

        gainNode.gain.setValueAtTime(0.0001, options.startTime);
        gainNode.gain.exponentialRampToValueAtTime(options.volume, options.startTime + 0.015);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, endTime);

        oscillator.connect(gainNode);
        gainNode.connect(context.destination);

        oscillator.start(options.startTime);
        oscillator.stop(endTime + 0.03);
    }

    isEnabled(): boolean {
        this.enabled = this.readEnabledFromStorage();
        return this.enabled;
    }

    setEnabled(enabled: boolean) {
        this.enabled = enabled;

        if (!this.isBrowser()) {
            return;
        }

        localStorage.setItem(SOUND_EFFECTS_KEY, String(enabled));
        localStorage.setItem(LEGACY_MUSIC_KEY, String(enabled));

        if (enabled) {
            this.prime();
        }
    }

    prime() {
        if (!this.isEnabled()) {
            return;
        }

        const context = this.getAudioContext();
        if (!context || context.state !== 'suspended') {
            return;
        }

        void context.resume().catch(() => undefined);
    }

    play(preset: SoundEffectPreset = 'tap') {
        if (!this.isEnabled()) {
            return;
        }

        const context = this.getAudioContext();
        if (!context) {
            return;
        }

        if (context.state === 'suspended') {
            void context.resume().catch(() => undefined);
            return;
        }

        const startTime = context.currentTime + 0.01;

        switch (preset) {
            case 'navigate':
                this.scheduleTone(context, {
                    startTime,
                    duration: 0.07,
                    fromFrequency: 420,
                    toFrequency: 620,
                    volume: 0.028,
                    type: 'sine'
                });
                this.scheduleTone(context, {
                    startTime: startTime + 0.045,
                    duration: 0.08,
                    fromFrequency: 620,
                    toFrequency: 860,
                    volume: 0.02,
                    type: 'triangle'
                });
                break;
            case 'toggle':
                this.scheduleTone(context, {
                    startTime,
                    duration: 0.09,
                    fromFrequency: 520,
                    toFrequency: 760,
                    volume: 0.024,
                    type: 'triangle'
                });
                break;
            case 'select':
                this.scheduleTone(context, {
                    startTime,
                    duration: 0.08,
                    fromFrequency: 460,
                    toFrequency: 680,
                    volume: 0.022,
                    type: 'sine'
                });
                break;
            case 'success':
                this.scheduleTone(context, {
                    startTime,
                    duration: 0.08,
                    fromFrequency: 520,
                    toFrequency: 740,
                    volume: 0.025,
                    type: 'triangle'
                });
                this.scheduleTone(context, {
                    startTime: startTime + 0.06,
                    duration: 0.1,
                    fromFrequency: 740,
                    toFrequency: 980,
                    volume: 0.022,
                    type: 'sine'
                });
                break;
            case 'tap':
            default:
                this.scheduleTone(context, {
                    startTime,
                    duration: 0.06,
                    fromFrequency: 640,
                    toFrequency: 420,
                    volume: 0.02,
                    type: 'triangle'
                });
                break;
        }
    }
}

export const SoundEffectsService = new SoundEffectsServiceClass();
